import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { EWorkbookItemType, EWorkbookStudyType, TClozeConversationPhase } from '@datatype/iyagigo/type';

interface IState {
  // ############### workbookKey -> selected choice ###############
  selectedClozeWorkbookChoice: Record<string, string>;
  selectedTestWorkbookChoice: Record<string, string>;

  // ############### 빈칸채우기/시험문제 학습기록 ###############
  clozeWorkbookStudyRecord: Record<string, { isCorrect: boolean | null; todayStudiedAt: string | null }>;
  testWorkbookStudyRecord: Record<string, { isCorrect: boolean | null; todayStudiedAt: string | null }>;

  // ############### workbookKey -> selected words (for grammar mode) ###############
  selectedGrammarWords: Record<string, { idx: number; word: string }[]>;

  // ############### 정답화면 ###############
  isClozeAnswerView: boolean;
  isTestAnswerView: boolean;

  // ############### 빈칸채우기/시험문제 완료 ###############
  isClozeComplete: boolean;
  isTestComplete: boolean;

  // ############### 빈칸채우기/시험문제 문제번호 ###############
  clozeWorkbookIdx: number;
  testWorkbookIdx: number;

  // ############### 빈칸채우기/시험문제 점수 ###############
  clozeScore: number;
  testScore: number;

  // ############### 빈칸채우기/시험문제 진행 여부 ###############
  isStudying: boolean;

  // ############### workbook 설정 ###############
  workbookStudyType: EWorkbookStudyType;
  workbookItemType: EWorkbookItemType;
  currentClozeNoteWorkbookKey: string | null;

  // ############### 대화연습 ###############
  isClozeConversationStudy: boolean;
  clozeConversationStudyStep: number;
  clozeConversationLineCursor: number;
  clozeConversationPhase: TClozeConversationPhase;
}

const initialState: IState = {
  isStudying: false,
  selectedClozeWorkbookChoice: {},
  selectedTestWorkbookChoice: {},
  clozeWorkbookStudyRecord: {},
  testWorkbookStudyRecord: {},
  selectedGrammarWords: {},
  isClozeAnswerView: false,
  isTestAnswerView: false,
  isClozeComplete: false,
  isTestComplete: false,
  clozeWorkbookIdx: 0,
  testWorkbookIdx: 0,
  clozeScore: 0,
  testScore: 0,
  workbookStudyType: EWorkbookStudyType.TODAY_STUDY,
  workbookItemType: EWorkbookItemType.BUNDLE,
  currentClozeNoteWorkbookKey: null,
  isClozeConversationStudy: false,
  clozeConversationStudyStep: 1,
  clozeConversationLineCursor: 0,
  clozeConversationPhase: { type: 'conversation-playing' },
};

const workbookSlice = createSlice({
  name: 'workbook',
  initialState,
  reducers: {
    initialize: (state) => {
      for (const key in state) {
        state[key] = initialState[key];
      }
    },
    storeIsStudying: (state, action: PayloadAction<boolean>) => {
      state.isStudying = action.payload;
    },
    storeWorkbookStudyType: (state, action: PayloadAction<EWorkbookStudyType>) => {
      state.workbookStudyType = action.payload;
    },
    storeIsClozeAnswerView: (state, action: PayloadAction<boolean>) => {
      state.isClozeAnswerView = action.payload;
    },
    storeIsTestAnswerView: (state, action: PayloadAction<boolean>) => {
      state.isTestAnswerView = action.payload;
    },
    storeIsClozeComplete: (state, action: PayloadAction<boolean>) => {
      state.isClozeComplete = action.payload;
    },
    storeIsTestComplete: (state, action: PayloadAction<boolean>) => {
      state.isTestComplete = action.payload;
    },
    storeClozeWorkbookIdx: (state, action: PayloadAction<number>) => {
      state.clozeWorkbookIdx = action.payload;
    },
    storeTestWorkbookIdx: (state, action: PayloadAction<number>) => {
      state.testWorkbookIdx = action.payload;
    },
    storeClozeScore: (state, action: PayloadAction<number>) => {
      state.clozeScore = action.payload;
    },
    storeTestScore: (state, action: PayloadAction<number>) => {
      state.testScore = action.payload;
    },

    // ############### cloze 어휘문제 보기선택 ###############
    selectClozeChoice: (
      state,
      action: PayloadAction<{
        workbookKey: string;
        selectedChoice: string;
      }>,
    ) => {
      const { workbookKey, selectedChoice } = action.payload;
      state.selectedClozeWorkbookChoice[workbookKey] = selectedChoice;
    },

    // ############### cloze, test 학습결과 저장 (회원/비회원 공용) ###############
    storeClozeStudyRecord: (
      state,
      action: PayloadAction<{
        workbookKey: string;
        isCorrect: boolean;
        todayStudiedAt: string;
      }>,
    ) => {
      const { workbookKey, isCorrect, todayStudiedAt } = action.payload;
      state.clozeWorkbookStudyRecord[workbookKey] = {
        isCorrect: isCorrect,
        todayStudiedAt: todayStudiedAt,
      };
    },

    syncClozeStudyRecord: (
      state,
      action: PayloadAction<{
        records: { key: string; isCorrect: boolean | null; todayStudiedAt: string | null }[];
        isAuthenticated: boolean;
      }>,
    ) => {
      const { records } = action.payload;

      const newRecord: Record<string, { isCorrect: boolean | null; todayStudiedAt: string | null }> = {};
      records.forEach((record) => {
        newRecord[record.key] = {
          isCorrect: record.isCorrect,
          todayStudiedAt: record.todayStudiedAt,
        };
      });
      state.clozeWorkbookStudyRecord = newRecord;
    },

    storeTestStudyRecord: (
      state,
      action: PayloadAction<{
        workbookKey: string;
        isCorrect: boolean;
        todayStudiedAt: string;
      }>,
    ) => {
      const { workbookKey, isCorrect, todayStudiedAt } = action.payload;
      state.testWorkbookStudyRecord[workbookKey] = {
        isCorrect: isCorrect,
        todayStudiedAt: todayStudiedAt,
      };
    },

    // ############### cloze 문법문제 단어선택 ###############
    selectClozeGrammarWords: (
      state,
      action: PayloadAction<{
        workbookKey: string;
        selectedWords: { idx: number; word: string }[];
      }>,
    ) => {
      const { workbookKey, selectedWords } = action.payload;
      state.selectedGrammarWords[workbookKey] = selectedWords;
    },

    // ############### workbook 설정 ###############
    configureTodayWorkbook: (state) => {
      state.workbookStudyType = EWorkbookStudyType.TODAY_STUDY;
      state.workbookItemType = EWorkbookItemType.BUNDLE;
      state.currentClozeNoteWorkbookKey = null;
    },
    configureReviewWorkbook: (state) => {
      state.workbookStudyType = EWorkbookStudyType.TODAY_REVIEW;
      state.workbookItemType = EWorkbookItemType.BUNDLE;
      state.currentClozeNoteWorkbookKey = null;
    },
    configureBookmarkWorkbook: (state, action: PayloadAction<{ workbookKey?: string }>) => {
      state.workbookStudyType = EWorkbookStudyType.BOOKMARK_NOTE;
      state.workbookItemType = action.payload.workbookKey ? EWorkbookItemType.SINGLE : EWorkbookItemType.BUNDLE;
      state.currentClozeNoteWorkbookKey = action.payload.workbookKey ?? null;
    },
    configureIncorrectWorkbook: (state, action: PayloadAction<{ workbookKey?: string }>) => {
      state.workbookStudyType = EWorkbookStudyType.INCORRECT_NOTE;
      state.workbookItemType = action.payload.workbookKey ? EWorkbookItemType.SINGLE : EWorkbookItemType.BUNDLE;
      state.currentClozeNoteWorkbookKey = action.payload.workbookKey ?? null;
    },

    // ############### 대화학습(연습) ###############
    storeIsClozeConversationStudy: (state, action: PayloadAction<boolean>) => {
      state.isClozeConversationStudy = action.payload;
    },
    storeClozeConversationStudyStep: (state, action: PayloadAction<number>) => {
      state.clozeConversationStudyStep = action.payload;
    },
    storeClozeConversationLineCursor: (state, action: PayloadAction<number>) => {
      state.clozeConversationLineCursor = action.payload;
    },
    incrementClozeConversationLineCursor: (state) => {
      state.clozeConversationLineCursor += 1;
    },
    storeClozeConversationPhase: (state, action: PayloadAction<TClozeConversationPhase>) => {
      state.clozeConversationPhase = action.payload;
    },
    resetClozeConversation: (state) => {
      state.clozeConversationLineCursor = 0;
      state.clozeConversationPhase = { type: 'conversation-playing' };
    },
    // 대화학습 전체 초기화 (step/진입여부까지). cloze 진입 시점에 호출.
    resetClozeConversationStudy: (state) => {
      state.isClozeConversationStudy = false;
      state.clozeConversationStudyStep = 1;
      state.clozeConversationLineCursor = 0;
      state.clozeConversationPhase = { type: 'conversation-playing' };
    },

    // ############### test 문제 보기선택 ###############
    selectTestChoice: (
      state,
      action: PayloadAction<{
        workbookKey: string;
        selectedChoice: string;
      }>,
    ) => {
      const { workbookKey, selectedChoice } = action.payload;
      state.selectedTestWorkbookChoice[workbookKey] = selectedChoice;
    },
  },
});

export const actions = workbookSlice.actions;
export default workbookSlice.reducer;
